/**
 * The checkout's reading of the server's 409 ITEM_UNAVAILABLE: the cart holds
 * lines that cannot be ordered — off the storefront's menu at the time the
 * order is for, sold out, or with an option or size gone. `/checkout` answers
 * it before any charge, and `/preview` answers the SAME body at review (mobile
 * parity API-1), so the guest is told before they reach the pay button rather
 * than seeing "Unable to Calculate Totals".
 *
 * The server writes the sentence and names each line (`itemUnavailableRefusal`
 * in apps/api); this only reads them.
 */

export interface ItemUnavailableNotice {
  /** The server's sentence, in the guest's words. */
  readonly message: string;
  readonly items: ReadonlyArray<{ readonly name: string; readonly reason: string }>;
}

/** The notice for an ITEM_UNAVAILABLE refusal, or `null` for any other error. */
export function itemUnavailableNoticeFrom(error: unknown): ItemUnavailableNotice | null {
  const e = error as { code?: string; message?: string; details?: { unavailableItems?: unknown } } | null;
  if (e?.code !== 'ITEM_UNAVAILABLE') return null;
  const listed = Array.isArray(e.details?.unavailableItems) ? e.details!.unavailableItems : [];
  return {
    message: (e.message ?? '').replace(/^[A-Z_]+: /, '') || 'Something in your cart is no longer available.',
    items: (listed as Array<{ name?: unknown; reason?: unknown }>)
      .filter((i) => typeof i?.name === 'string')
      .map((i) => ({ name: i.name as string, reason: typeof i.reason === 'string' ? i.reason : 'sold_out' })),
  };
}
